/**
 * The days a family confirmed for their Flex enrollments this month.
 *
 * Flex bills on a plan, not on a cron: the schedule says which weekdays are *available* and
 * the parent picks from them, so the only honest source for "does this child have Flex today"
 * is `flex_month_plans`. Every other billing type answers the question from its schedule and
 * never reaches this hook.
 *
 * One row per (enrollment, month), so this is a single indexed read of at most a handful of
 * rows, and it does not run at all for the great majority of families who have no Flex
 * enrollment — `enabled` is false when the id list is empty. Cached for the session: a plan
 * for the month in progress is locked, and the rare mid-month admin edit is not worth
 * polling every dashboard in the school for.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/** `YYYY-MM-01` for the month containing a `YYYY-MM-DD` day — the shape `plan_month` takes. */
function monthOf(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/** Confirmed dates, keyed by enrollment. Missing key = no plan on file = no day confirmed. */
export type FlexPlanDates = Record<string, string[]>;

/**
 * @param days One day, or every day whose month should be covered. The Today card wants a
 *   single day; the week timetable passes its seven, because a week straddling a month
 *   boundary needs both plans — reading one month would blank the other's days, which for a
 *   plan-driven enrollment reads as "not bought" rather than "not loaded".
 */
export function useFlexPlanDates(enrollmentIds: string[], days: string | string[]) {
  // Sorted so two renders with the same enrollments in a different order share one cache
  // entry rather than firing the query twice.
  const ids = [...enrollmentIds].sort();
  const dayList = (Array.isArray(days) ? days : [days]).filter(Boolean);
  const months = [...new Set(dayList.map(monthOf))].sort();

  const query = useQuery<FlexPlanDates>({
    queryKey: ['flex-plan-dates', months, ids],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flex_month_plans')
        .select('enrollment_id, dates')
        .in('enrollment_id', ids)
        .in('plan_month', months);
      if (error) throw error;

      // Merged across months, so a straddling week sees both halves under one key.
      const byEnrollment: FlexPlanDates = {};
      for (const row of (data ?? []) as { enrollment_id: string; dates: string[] | null }[]) {
        byEnrollment[row.enrollment_id] = [
          ...(byEnrollment[row.enrollment_id] ?? []),
          ...(row.dates ?? []),
        ];
      }
      return byEnrollment;
    },
    enabled: ids.length > 0 && months.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // A disabled query's data is null, not undefined — the caller wants a lookup table either
  // way, and an empty one correctly says "no day confirmed".
  return query.data ?? {};
}
